import { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  InputGroup,
  Spinner,
} from "react-bootstrap";
import { useForm } from "react-hook-form";
import axios from "axios";
import { toast } from "react-toastify";
import { BASE_URL, HEADERS } from "../../../utils/constant";
import { FaCalendarAlt } from "react-icons/fa";
import dayjs from "dayjs";
import { useCommonContext } from "../../../utils/context";

const ApplyLeaveModal = ({
  show,
  handleClose,
  leaveData,
  onApplyLeave,
  isUpdateStatus,
  fetchuserleave,
}) => {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const { user } = useCommonContext;

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
    getValues,
    setError,
    clearErrors,
  } = useForm();

  useEffect(() => {
    if (leaveData) {
      setValue("start_date", leaveData.start_date);
      setValue("end_date", leaveData.end_date);
      setValue("reason", leaveData.reason);
      setValue("status", leaveData.status);
      setValue("leave_type", leaveData.leave_type || "");
    } else {
      reset();
    }
  }, [leaveData, setValue, reset]);

  const onSubmit = async (data) => {
    const userId = localStorage.getItem("userId");

    const payload = isUpdateStatus
      ? {
          status: data.status,
          ...(data.status === "Approved" && {
            approved_remark: data.approved_remark,
          }),
          ...(data.status === "Rejected" && {
            rejected_remark: data.rejected_remark,
          }),
        }
      : {
          start_date: data.start_date,
          end_date: data.end_date,
          reason: data.reason,
          leave_type: data.leave_type,
        };

    if (!leaveData) {
      payload.userId = userId;
    }

    try {
      setLoading(true);
      if (leaveData) {
        await axios.put(`${BASE_URL}/users/leaves/${leaveData.id}/`, payload, {
          headers: HEADERS,
        });
        toast.success(
          isUpdateStatus
            ? "Status updated successfully."
            : "Leave updated successfully.",
        );
        fetchuserleave();
      } else {
        await axios.post(`${BASE_URL}/users/leaves/`, payload, {
          headers: HEADERS,
        });
        toast.success("Leave applied successfully.");
      }

      onApplyLeave();
      handleClose();
    } catch (error) {
      const errorMessage =
        error.response?.data?.error || "Something went wrong!";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const validateDates = () => {
    const { start_date, end_date } = getValues();

    const today = dayjs().startOf("day");
    const startDate = dayjs(start_date);
    const endDate = dayjs(end_date);

    clearErrors("start_date");
    clearErrors("end_date");

    if (startDate.isBefore(today)) {
      setError("start_date", {
        type: "manual",
        message: "Start date cannot be in the past.",
      });
    }

    if (endDate.isBefore(today)) {
      setError("end_date", {
        type: "manual",
        message: "End date cannot be in the past.",
      });
    }

    if (startDate.isAfter(endDate)) {
      setError("start_date", {
        type: "manual",
        message: "Start date cannot be after end date.",
      });
      setError("end_date", {
        type: "manual",
        message: "End date cannot be before start date.",
      });
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton className="border-0">
        <Modal.Title className="w-100 text-center">
          {isUpdateStatus ? "Update Leave Status" : "Apply for Leave"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4">
        {/* <div className="mb-3">
          <h6 className="text-primary">
            Remaining Casual Leaves:{" "}P
            <strong>
              {user?.allocated_casual_leave ??
                leaveData?.userId?.allocated_casual_leave ??
                "0"}
            </strong>
          </h6>

          <h6 className="text-success">
            Remaining Sick Leaves:{" "}
            <strong>
              {user?.allocated_sick_leave ??
                leaveData?.userId?.allocated_sick_leave ??
                "0"}
            </strong>
          </h6>
        </div> */}

        <Form onSubmit={handleSubmit(onSubmit)}>
          {!isUpdateStatus && (
            <>
              <Form.Group controlId="leave_type" className="mb-3">
                <Form.Label>Leave Type*</Form.Label>
                <Form.Control
                  as="select"
                  {...register("leave_type", { required: true })}
                  defaultValue=""
                >
                  <option value="">Select Leave Type</option>
                  <option value="Sick">Sick Leave</option>
                  <option value="Casual">Casual Leave</option>
                </Form.Control>
                {errors.leave_type && (
                  <span className="text-danger">Leave type is required</span>
                )}
              </Form.Group>

              <Row>
                <Col>
                  <Form.Group controlId="startDate" className="mb-3">
                    <Form.Label>Start Date*</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <FaCalendarAlt />
                      </InputGroup.Text>
                      <Form.Control
                        type="date"
                        {...register("start_date", {
                          required: "Start date is required",
                          validate: validateDates,
                        })}
                      />
                    </InputGroup>
                    {errors.start_date && (
                      <span className="text-danger">
                        {errors.start_date.message}
                      </span>
                    )}
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group controlId="endDate" className="mb-3">
                    <Form.Label>End Date*</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <FaCalendarAlt />
                      </InputGroup.Text>
                      <Form.Control
                        type="date"
                        {...register("end_date", {
                          required: "End date is required",
                          validate: validateDates,
                        })}
                      />
                    </InputGroup>
                    {errors.end_date && (
                      <span className="text-danger">
                        {errors.end_date.message}
                      </span>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group controlId="reason" className="mb-3">
                <Form.Label>Reason*</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  {...register("reason", { required: true })}
                  placeholder="Enter the reason for leave"
                />
                {errors.reason && (
                  <span className="text-danger">Reason is required</span>
                )}
              </Form.Group>
            </>
          )}

          {isUpdateStatus && (
            <>
              <Form.Group controlId="status" className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Control
                  as="select"
                  {...register("status", { required: true })}
                  defaultValue=""
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="Submitted">Submitted</option>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                </Form.Control>
                {errors.status && (
                  <span className="text-danger">Status is required</span>
                )}
              </Form.Group>

              {status === "Approved" && (
                <Form.Group controlId="approved_remark" className="mb-3">
                  <Form.Label>Approved Remark</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    {...register("approved_remark", { required: true })}
                    placeholder="Enter remark for approval"
                  />
                  {errors.approved_remark && (
                    <span className="text-danger">Remark is required</span>
                  )}
                </Form.Group>
              )}

              {status === "Rejected" && (
                <Form.Group controlId="rejected_remark" className="mb-3">
                  <Form.Label>Rejected Remark</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    {...register("rejected_remark", { required: true })}
                    placeholder="Enter remark for rejection"
                  />
                  {errors.rejected_remark && (
                    <span className="text-danger">Remark is required</span>
                  )}
                </Form.Group>
              )}
            </>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer className="justify-content-end border-0">
        <Button
          variant="outline-secondary"
          onClick={handleClose}
          className="me-2"
        >
          Close
        </Button>

        <Button
          variant="primary"
          onClick={handleSubmit(onSubmit)}
          disabled={loading}
        >
          {loading ? (
            <Spinner animation="border" role="status" size="sm">
              <span className="visually-hidden"></span>
            </Spinner>
          ) : isUpdateStatus ? (
            "Submit"
          ) : (
            "Submit"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ApplyLeaveModal;
